import { selectRotationQuestions } from '../utils/questionHistoryPolicy.js';
// ✅ COMPLETE SECURE useAssessment.js with TIMER FIXES INTEGRATED
// 🔒 ALL SECURITY VULNERABILITIES FIXED + ASSESSMENT POOL BUG FIXED + TIMER ISSUES FIXED
// ✅ POOLED QUESTION PERSISTENCE FIX - Questions saved to server for cross-session recovery

import { useState, useEffect, useCallback, useRef } from 'react';
import { PerformanceModel } from '../db/models/performanceModel';
import { ResponseModel } from '../db/models/responseModel';
import { api } from '../api/client';
import { assessmentIdOf, isEnabledFlag } from '../utils/assessmentIdentity';
import { deriveLearnerAlias, isLearnerAlias } from '../utils/learnerIdentity';
import {
  boundedOverallTimeSeconds,
  normalizeTimerMode,
  normalizeTimingPolicy,
  normalizeOverallTimeStatus
} from '../utils/assessmentTiming';

const API_BASE_URL = '/api';
const NEXT_ASSESSMENT_INTENT_KEY = 'assessnow-next-assessment-intent-v1';
const NEXT_ASSESSMENT_INTENT_TTL_MS = 30 * 60 * 1000;

const readNextAssessmentIntent = () => {
  if (typeof sessionStorage === 'undefined') return null;
  try {
    const value = JSON.parse(sessionStorage.getItem(NEXT_ASSESSMENT_INTENT_KEY) || 'null');
    if (
      !value ||
      !isLearnerAlias(value.learnerAlias) ||
      !String(value.gradeName || '').trim() ||
      Date.now() - Number(value.createdAt || 0) > NEXT_ASSESSMENT_INTENT_TTL_MS
    ) {
      sessionStorage.removeItem(NEXT_ASSESSMENT_INTENT_KEY);
      return null;
    }
    return value;
  } catch {
    sessionStorage.removeItem(NEXT_ASSESSMENT_INTENT_KEY);
    return null;
  }
};

const persistNextAssessmentIntent = value => {
  if (typeof sessionStorage === 'undefined') return;
  try {
    if (value) sessionStorage.setItem(NEXT_ASSESSMENT_INTENT_KEY, JSON.stringify(value));
    else sessionStorage.removeItem(NEXT_ASSESSMENT_INTENT_KEY);
  } catch {
    // Session recovery is best-effort; the in-memory transition remains valid.
  }
};

const validateAnswersBatch = async (answers, assessmentFileName, accessCode, performanceId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/validate-answers-batch`, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        answers,
        assessmentFileName,
        accessCode,
        performanceId
      })
    });

    if (!response.ok) {
      throw new Error(`Batch validation failed: ${response.status}`);
    }

    const result = await response.json();
    return result.results;
  } catch (error) {
    console.error('Error in batch answer validation:', error);
    return {};
  }
};

// FIXED VERSION - Replace the existing questionHistoryAPI object with this:
const questionHistoryAPI = {
  // Save question history to server
  async saveHistory(accessCode, assessmentId, questionIds, selectionKey, studentInfo = null, definitionChecksum = null) {
    try {
      const response = await fetch(`${API_BASE_URL}/question-history`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          accessCode,
          assessmentId,
          questionIds,
          selectionKey,
          studentInfo,
          ...(definitionChecksum ? { definitionChecksum } : {})
        })
      });

      if (!response.ok) {
        const failure = await response.json().catch(() => ({}));
        throw new Error(failure.error || 'Rotation history could not be saved');
      }

      return await response.json();
    } catch (error) {
      console.error('Error saving question history:', error);
      throw new Error(`${error.message || 'Rotation history could not be confirmed'}. The selected question set is saved. Check the connection and retry before starting.`);
    }
  },

  // Get question history from server
  async getHistory(accessCode, assessmentId) {
    try {
      const response = await fetch(
        `${API_BASE_URL}/question-history/${encodeURIComponent(assessmentId)}`
      );

      if (!response.ok) {
        throw new Error('Failed to fetch question history');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching question history:', error);
      throw new Error('Question rotation history is unavailable. Check the connection and retry. Your previous assessment results are preserved.');
    }
  },

  // Get smart question selection from server
  async getSmartSelection(accessCode, assessmentId, availableQuestions, totalToSelect, seed) {
    try {
      const response = await fetch(`${API_BASE_URL}/question-history/smart-select`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          accessCode,
          assessmentId,
          availableQuestions,
          totalToSelect,
          seed
        })
      });

      if (!response.ok) {
        throw new Error('Failed to get smart selection');
      }

      const result = await response.json();
      return result.selectedQuestions;
    } catch (error) {
      console.error('Error getting smart selection:', error);
      // Fallback to local selection
      return selectQuestionsWithTopicDistribution(
        availableQuestions, 
        totalToSelect, 
        seed
      );
    }
  }
};

// ----- Name Formatting Function -----
const formatStudentNameForDatabase = (originalName, gradeLevel, accessCode) => {
  if (isLearnerAlias(originalName)) return String(originalName).trim();
  if (!originalName || !gradeLevel || !accessCode) {
    console.warn('Missing required parameters for name formatting:', {
      originalName, gradeLevel, accessCode
    });
    return originalName;
  }

  try {
    return deriveLearnerAlias(originalName, gradeLevel, accessCode) || originalName;
  } catch (error) {
    console.error('Error formatting student name:', error);
    return originalName;
  }
};

// ✅ SECURITY: Enhanced PooledQuestionManager WITHOUT correct answers
// ✅ FIXED: Added server persistence for cross-browser-session recovery
class SecurePooledQuestionManager {
  constructor() {
    this.storageKey = 'pooled_assessment_questions';
    this.init();
  }

  init() {
    try {
      const stored = sessionStorage.getItem(this.storageKey);
      this.questionSets = stored ? JSON.parse(stored) : {};
    } catch (e) {
      console.error('Error initializing pooled question manager:', e);
      this.questionSets = {};
    }
  }

  saveQuestionSet(continueCode, questions) {
    if (!continueCode || !questions || !Array.isArray(questions)) {
      console.error('Invalid parameters for saveQuestionSet');
      return false;
    }

    try {
      // ✅ SECURITY: Save question data WITHOUT correct answers
      const secureData = questions.map(q => ({
        ItemNumber: q.ItemNumber,
        Question: q.Question,
        // ❌ REMOVED: CorrectAnswer: q.CorrectAnswer,
        questionType: q.questionType,
        QuestionType: q.QuestionType,
        Options: q.Options,
        Direction: q.Direction,
        ReadingPassage: q.ReadingPassage,
        ReadingPassages: q.ReadingPassages,
        Image: q.Image,
        image: q.image,
        Topic1: q.Topic1 || '',
        Topic2: q.Topic2 || '',
        Topic3: q.Topic3 || '',
        Topic4: q.Topic4 || '',
        Topic5: q.Topic5 || '',
        // ✅ NEW: Preserve writing composition fields
        RubricCriteria: q.RubricCriteria || null,
        PassingThreshold: q.PassingThreshold || null
      }));

      this.questionSets[continueCode] = secureData;
      sessionStorage.setItem(this.storageKey, JSON.stringify(this.questionSets));
      
      this.saveQuestionSetToDatabase(continueCode, secureData);
      
      return true;
    } catch (e) {
      console.error('Error saving question set:', e);
      return false;
    }
  }

  async saveQuestionSetToDatabase(continueCode, questions) {
    try {
      const metadata = {
        selectedQuestionIds: questions.map(q => q.ItemNumber),
        selectionTimestamp: new Date().toISOString(),
        totalQuestions: questions.length,
        selectedQuestions: questions
      };
      
      sessionStorage.setItem(`assessment_metadata_${continueCode}`, 
        JSON.stringify(metadata));
    } catch (error) {
      console.warn('Could not save question set to database backup:', error);
    }
  }

  getQuestionSet(continueCode) {
    if (!continueCode) return null;
    return this.questionSets[continueCode] || null;
  }

  async recoverQuestionSet(continueCode, originalQuestions, performanceId) {
    
    let savedQuestions = this.getQuestionSet(continueCode);
    if (savedQuestions && savedQuestions.length > 0) {
      return savedQuestions;
    }

    try {
      const metadataStr = sessionStorage.getItem(`assessment_metadata_${continueCode}`);
      if (metadataStr) {
        const metadata = JSON.parse(metadataStr);
        if (metadata.selectedQuestions && metadata.selectedQuestions.length > 0) {
          this.questionSets[continueCode] = metadata.selectedQuestions;
          sessionStorage.setItem(this.storageKey, JSON.stringify(this.questionSets));
          return metadata.selectedQuestions;
        }
      }
    } catch (error) {
      console.warn('Could not recover from database metadata:', error);
    }

    if (performanceId && originalQuestions) {
      try {
        const response = await fetch(`${API_BASE_URL}/responses/${performanceId}`);
        if (response.ok) {
          const responseData = await response.json();
          if (responseData?.responses) {
            const answeredQuestionIds = Object.keys(responseData.responses).map(String);
            
            // ✅ SECURITY: Strip answers from original questions before processing
            const secureOriginalQuestions = originalQuestions.map(q => ({
              ...q,
              CorrectAnswer: undefined
            }));
            
            const answeredQuestions = secureOriginalQuestions.filter(q => 
              answeredQuestionIds.includes(String(q.ItemNumber))
            );
            
            if (answeredQuestions.length > 0) {
                
              const remainingCount = 10 - answeredQuestions.length;
              const unansweredQuestions = secureOriginalQuestions.filter(q => 
                !answeredQuestionIds.includes(String(q.ItemNumber))
              );
              
              let reconstructedSet = [...answeredQuestions];
              
              if (remainingCount > 0 && unansweredQuestions.length > 0) {
                const seed = parseInt(continueCode) || 12345;
                const additionalQuestions = this.getSeededRandomQuestions(
                  unansweredQuestions, remainingCount, seed
                );
                reconstructedSet = [...reconstructedSet, ...additionalQuestions];
              }
              
              this.saveQuestionSet(continueCode, reconstructedSet);
              return reconstructedSet;
            }
          }
        }
      } catch (error) {
        console.error('Error reconstructing from responses:', error);
      }
    }
    return null;
  }

  getSeededRandomQuestions(questions, count, seed) {
    const seededRandom = (max, min = 0) => {
      seed = (seed * 9301 + 49297) % 233280;
      const rnd = seed / 233280;
      return min + rnd * (max - min);
    };

    const questionsCopy = [...questions];
    const result = [];
    const numToSelect = Math.min(count, questionsCopy.length);

    for (let i = 0; i < numToSelect; i++) {
      const randomIndex = Math.floor(seededRandom(questionsCopy.length));
      result.push(questionsCopy[randomIndex]);
      questionsCopy.splice(randomIndex, 1);
    }
    return result;
  }

  hasQuestionSet(continueCode) {
    return !!this.getQuestionSet(continueCode);
  }

  clearQuestionSet(continueCode) {
    if (this.questionSets[continueCode]) {
      delete this.questionSets[continueCode];
      sessionStorage.setItem(this.storageKey, JSON.stringify(this.questionSets));
      
      try {
        sessionStorage.removeItem(`assessment_metadata_${continueCode}`);
      } catch (error) {
        console.warn('Could not clear database backup:', error);
      }
      return true;
    }
    return false;
  }

  // ✅ FIXED: Save pooled questions to server for persistence across browser sessions
  // ✅ Using full API_BASE_URL instead of relative path
  async saveToServer(continueCode, questions, accessCode) {
    if (!continueCode || !questions || !accessCode) {
      console.warn('⚠️ Missing parameters for server save:', { continueCode: !!continueCode, questions: !!questions, accessCode: !!accessCode });
      return false;
    }
    
    try {
      const questionIds = questions.map(q => q.ItemNumber);
      
      // ✅ FIXED: Use full API_BASE_URL instead of relative path
      const response = await fetch(`${API_BASE_URL}/sessions/update-pooled-questions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          continueCode,
          accessCode,
          pooledQuestionIds: questionIds
        })
      });
      
      if (response.ok) {
        return true;
      } else {
        const errorText = await response.text();
        console.warn('❌ Failed to save pooled questions to server:', response.status, errorText);
        return false;
      }
    } catch (error) {
      console.warn('❌ Error saving pooled questions to server:', error);
      return false;
    }
  }

  // ✅ FIXED: Recover pooled questions from server
  // ✅ Using full API_BASE_URL instead of relative path
  async recoverFromServer(continueCode, accessCode, fullQuestions) {
    if (!continueCode || !accessCode) {
      console.warn('⚠️ Missing continueCode or accessCode for server recovery');
      return null;
    }
    
    try {
      
      // ✅ FIXED: Use full API_BASE_URL instead of relative path
      const response = await fetch(`${API_BASE_URL}/sessions/pooled-questions/${continueCode}`);
      
      if (!response.ok) {
        const failure = await response.json().catch(() => ({}));
        if ([409, 422].includes(response.status)) {
          throw new Error(failure.error || 'The saved pooled question set failed its integrity check');
        }
        console.warn(`⚠️ Server returned ${response.status} for pooled questions`);
        return null;
      }
      
      const data = await response.json();
      
      if (data.pooledQuestionIds && Array.isArray(data.pooledQuestionIds) && data.pooledQuestionIds.length > 0) {
        
        // Reconstruct questions from IDs, maintaining order
        const recoveredQuestions = data.pooledQuestionIds
          .map(id => fullQuestions.find(q => q.ItemNumber === id || String(q.ItemNumber) === String(id)))
          .filter(q => q !== undefined);
        
        if (recoveredQuestions.length > 0) {
          
          // Also save to local storage for faster access
          this.saveQuestionSet(continueCode, recoveredQuestions);
          
          return recoveredQuestions;
        } else {
          console.warn('⚠️ Could not match any question IDs from server to full questions');
        }
      } else {
      }
    } catch (error) {
      console.warn('❌ Error recovering pooled questions from server:', error);
    }
    
    return null;
  }
}

const securePooledQuestionManager = new SecurePooledQuestionManager();

function getSeededRandomQuestions(questions, count, seed) {
  const seededRandom = (max, min = 0) => {
    seed = (seed * 9301 + 49297) % 233280;
    const rnd = seed / 233280;
    return min + rnd * (max - min);
  };

  const questionsCopy = [...questions];
  const result = [];
  const numToSelect = Math.min(count, questionsCopy.length);

  for (let i = 0; i < numToSelect; i++) {
    const randomIndex = Math.floor(seededRandom(questionsCopy.length));
    result.push(questionsCopy[randomIndex]);
    questionsCopy.splice(randomIndex, 1);
  }
  return result;
}

const cleanupPooledSession = (continueCode) => {
  if (continueCode) {
    securePooledQuestionManager.clearQuestionSet(continueCode);
  }
};

// ----- Topic-Aware Helper Functions -----
const getUniqueTopics = (questions) => {
  const topicsSet = new Set();
  
  questions.forEach(q => {
    [q.Topic1, q.Topic2, q.Topic3, q.Topic4, q.Topic5].forEach(topic => {
      if (topic && topic.trim()) {
        topicsSet.add(topic.trim());
      }
    });
  });
  
  return Array.from(topicsSet);
};

const groupQuestionsByTopic = (questions) => {
  const topicGroups = {};
  
  questions.forEach(q => {
    const questionTopics = [q.Topic1, q.Topic2, q.Topic3, q.Topic4, q.Topic5]
      .filter(topic => topic && topic.trim())
      .map(topic => topic.trim());
    
    if (questionTopics.length > 0) {
      questionTopics.forEach(topic => {
        if (!topicGroups[topic]) {
          topicGroups[topic] = [];
        }
        topicGroups[topic].push(q);
      });
    } else {
      if (!topicGroups['general']) {
        topicGroups['general'] = [];
      }
      topicGroups['general'].push(q);
    }
  });
  
  return topicGroups;
};

// ✅ FIXED: Enhanced selectQuestionsWithTopicDistribution function
// This fixes the bug where only 9 questions were selected instead of 10
const selectQuestionsWithTopicDistribution = (questions, totalToSelect = 10, seed = null) => {
  
  // Early return if not enough questions or no questions
  if (!questions || questions.length === 0) {
    console.warn('❌ No questions provided');
    return [];
  }
  
  if (questions.length <= totalToSelect) {
    return seed ? getSeededRandomQuestions(questions, questions.length, seed) : deepCopyAndShuffle(questions, questions.length);
  }
  
  const uniqueTopics = getUniqueTopics(questions);
  
  // If no topics, just select randomly
  if (uniqueTopics.length === 0) {
    const result = seed ? getSeededRandomQuestions(questions, totalToSelect, seed) : deepCopyAndShuffle(questions, totalToSelect);
    return result;
  }
  
  const topicGroups = groupQuestionsByTopic(questions);
  const topicNames = Object.keys(topicGroups);
  const numTopics = topicNames.length;
    
  const selectedQuestions = [];
  const usedQuestionIds = new Set(); // Track used question IDs to prevent duplicates
  
  // ✅ PHASE 1: Ensure at least 1 question from each topic (if possible)
  topicNames.forEach((topic, index) => {
    const questionsForTopic = topicGroups[topic].filter(q => !usedQuestionIds.has(q.ItemNumber));
    if (questionsForTopic.length > 0 && selectedQuestions.length < totalToSelect) {
      let selected;
      if (seed) {
        selected = getSeededRandomQuestions(questionsForTopic, 1, seed + index);
      } else {
        selected = deepCopyAndShuffle(questionsForTopic, 1);
      }
      
      if (selected.length > 0) {
        selectedQuestions.push(selected[0]);
        usedQuestionIds.add(selected[0].ItemNumber);
      }
    }
  });
  
  // ✅ PHASE 2: Distribute remaining questions across topics
  const remainingToSelect = totalToSelect - selectedQuestions.length;
  
  if (remainingToSelect > 0) {
    // Calculate how many additional questions each topic should get
    const basePerTopic = Math.floor(remainingToSelect / numTopics);
    const extraQuestions = remainingToSelect % numTopics;
        
    // Distribute additional questions
    topicNames.forEach((topic, index) => {
      if (selectedQuestions.length >= totalToSelect) return; // Safety check
      
      const questionsForTopic = topicGroups[topic].filter(q => !usedQuestionIds.has(q.ItemNumber));
      let numToSelectFromTopic = basePerTopic;
      
      // Give extra questions to first few topics
      if (index < extraQuestions) {
        numToSelectFromTopic += 1;
      }
      
      // Don't exceed available questions for this topic
      numToSelectFromTopic = Math.min(numToSelectFromTopic, questionsForTopic.length);
      
      // Don't exceed total target
      numToSelectFromTopic = Math.min(numToSelectFromTopic, totalToSelect - selectedQuestions.length);
      
      if (numToSelectFromTopic > 0) {
        let topicSelected;
        if (seed) {
          topicSelected = getSeededRandomQuestions(questionsForTopic, numToSelectFromTopic, seed + index + 100);
        } else {
          topicSelected = deepCopyAndShuffle(questionsForTopic, numToSelectFromTopic);
        }
        
        // Add questions and track IDs
        topicSelected.forEach(q => {
          if (selectedQuestions.length < totalToSelect && !usedQuestionIds.has(q.ItemNumber)) {
            selectedQuestions.push(q);
            usedQuestionIds.add(q.ItemNumber);
          }
        });
      }
    });
  }
    
  // ✅ PHASE 3: Fill remaining slots if we're still short (safety net)
  if (selectedQuestions.length < totalToSelect) {
    const stillRemaining = totalToSelect - selectedQuestions.length;
    
    const unusedQuestions = questions.filter(q => !usedQuestionIds.has(q.ItemNumber));
    
    if (unusedQuestions.length > 0) {
      let finalSelected;
      if (seed) {
        finalSelected = getSeededRandomQuestions(unusedQuestions, stillRemaining, seed + 1000);
      } else {
        finalSelected = deepCopyAndShuffle(unusedQuestions, stillRemaining);
      }
      
      finalSelected.forEach(q => {
        if (selectedQuestions.length < totalToSelect && !usedQuestionIds.has(q.ItemNumber)) {
          selectedQuestions.push(q);
          usedQuestionIds.add(q.ItemNumber);
        }
      });
    }
  }
  
  // ✅ PHASE 4: Final shuffle to mix topics
  let finalSelection;
  if (seed) {
    finalSelection = getSeededRandomQuestions(selectedQuestions, selectedQuestions.length, seed + 2000);
  } else {
    finalSelection = deepCopyAndShuffle(selectedQuestions, selectedQuestions.length);
  }
  
  // ✅ VERIFICATION: Ensure we have exactly the right number
  const actualCount = finalSelection.length;
  const targetCount = Math.min(totalToSelect, questions.length);
  
  if (actualCount !== targetCount) {
    console.error(`❌ CRITICAL: Expected ${targetCount} questions but got ${actualCount}`);
    console.error(`❌ Selected question IDs:`, finalSelection.map(q => q.ItemNumber));
    
    // Emergency fallback - just select randomly
    return seed ? getSeededRandomQuestions(questions, targetCount, seed) : deepCopyAndShuffle(questions, targetCount);
  }
  
  // Log final topic distribution
  const finalTopics = getUniqueTopics(finalSelection);
  const finalGroups = groupQuestionsByTopic(finalSelection);
  return finalSelection;
};

// Helper functions
const API_CHECK_IMAGE = async (url) => {
  const fullUrl = url.startsWith('http') ? url : (url.startsWith('/') ? url : `/${url}`);
  try {
    const response = await fetch(fullUrl, { method: 'HEAD' });
    return response.ok;
  } catch (error) {
    console.error(`Error checking if image exists at ${fullUrl}:`, error);
    return false;
  }
};

const getRandomSubset = (arr, n) => {
  const shuffled = arr.slice();
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled.slice(0, n);
};

const handleAnswerSubmission = (questionType, value, correctAnswer) => {
  return value;
};

const deepCopyAndShuffle = (arr, n) => {
  const shuffled = JSON.parse(JSON.stringify(arr));
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled.slice(0, n);
};

// ✅ FIXED: Better assessment pool determination
const determineAssessmentPool = (assessmentData, selectedAssessment) => {
  let assessmentPool = 'NA';
  
  // Priority 1: Use selectedAssessment.assessmentPool (from database)
  if (selectedAssessment?.assessmentPool && selectedAssessment.assessmentPool.trim()) {
    assessmentPool = selectedAssessment.assessmentPool.trim();
    return assessmentPool;
  }
  
  // Priority 2: Use assessmentData.assessmentPool (from file)
  if (assessmentData?.assessmentPool && assessmentData.assessmentPool.trim()) {
    assessmentPool = assessmentData.assessmentPool.trim();
    return assessmentPool;
  }
  
  // The selected row is the exact server catalog record. Never perform a
  // second name/file lookup here; it adds latency and can cross identities.
  // If an old file lacks pool metadata, the performance endpoint revalidates
  // the canonical catalog record.
  if (assessmentData?.assessmentKind === 'regular') {
    // ✅ FIXED: Don't default to 'NonPooled' - let server determine
    return 'NA'; // Let server determine from assessment settings
  }
  
  return assessmentPool;
};

// ✅ MAIN HOOK: useAssessment with complete security fixes + TIMER FIXES
export const useAssessment = () => {
  const [assessmentData, setAssessmentData] = useState(null);
  const [currentGradeIndex, setCurrentGradeIndex] = useState(1);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(1);
  const [studentResponses, setStudentResponses] = useState({});
  const [correctAnswers, setCorrectAnswers] = useState({});
  const [consecutiveFailures, setConsecutiveFailures] = useState(0);
  const [maxCorrectCount, setMaxCorrectCount] = useState(0);
  const [performanceGradeLevel, setPerformanceGradeLevel] = useState('');
  const [isComplete, setIsComplete] = useState(false);
  const [totalGrades, setTotalGrades] = useState(0);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [studentName, setStudentName] = useState('');
  const [studentGrade, setStudentGrade] = useState('');
  const [assessmentType, setAssessmentType] = useState('');
  const [currentPerformanceId, setCurrentPerformanceId] = useState(null);
  const finalizationInFlightRef = useRef(null);
  const sessionViewEpochRef = useRef('');
  const rotationDefinitionRef = useRef(null);
  const [accessCode, setAccessCode] = useState('');
  const [assessmentKind, setAssessmentKind] = useState('gradeLevel');
  const [gradeScores, setGradeScores] = useState({});
  const [highestPassedGrade, setHighestPassedGrade] = useState('');
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const [sessionContinueCode, setSessionContinueCode] = useState('');
  const [inputStates, setInputStates] = useState({});
  const [evaluatedBlocks, setEvaluatedBlocks] = useState([]);
  const [isResumedSession, setIsResumedSession] = useState(false);
  const [isAccessVerified, setIsAccessVerified] = useState(false);
  // ✅ NEW: Add timer state management
  const [timerSeconds, setTimerSeconds] = useState(null);
  const [timerMode, setTimerMode] = useState('off');
  const [timingPolicy, setTimingPolicy] = useState(null);
  const [overallTiming, setOverallTiming] = useState(() => normalizeOverallTimeStatus());
  const [timerSettingsLoaded, setTimerSettingsLoaded] = useState(false);
  // ✅ NEW: Track pooled questions for saving after session creation
  const [pendingPooledQuestions, setPendingPooledQuestions] = useState(null);
  // ✅ NEW: Writing composition rubric results storage
  const [writingResults, setWritingResults] = useState({});
  const [itemTiming, setItemTiming] = useState({});
  const [writingDrafts, setWritingDrafts] = useState({});
  const [nextAssessmentIntent, setNextAssessmentIntent] = useState(readNextAssessmentIntent);

  useEffect(() => {
    setIsResumedSession(!!sessionContinueCode);
  }, [sessionContinueCode]);

  // Rebuild adaptive state from the exact stored responses. Without this, a
  // resumed grade-level attempt can forget earlier 3-of-5 block outcomes.
  useEffect(() => {
    if (!isResumedSession || assessmentKind !== 'gradeLevel' || !assessmentData) return;
    let failures = 0;
    let highest = '';
    const completedBlocks = [];
    for (let index = 0; index < assessmentData.Grades.length; index += 1) {
      const grade = assessmentData.Grades[index];
      const questions = grade.Questions || [];
      const complete = questions.length > 0 && questions.every(question => (
        Object.prototype.hasOwnProperty.call(studentResponses, String(question.ItemNumber))
      ));
      if (!complete) break;
      completedBlocks.push(index);
      const correct = questions.filter(question => (
        correctAnswers[String(question.ItemNumber)] === true
      )).length;
      if (correct >= 3) {
        failures = 0;
        highest = grade.Grade;
      } else {
        failures += 1;
      }
    }
    setEvaluatedBlocks(completedBlocks);
    setConsecutiveFailures(failures);
    setHighestPassedGrade(highest);
  }, [isResumedSession, assessmentKind, assessmentData, studentResponses, correctAnswers]);

  // A visible assessment periodically reconciles both activity and the actual
  // on-screen item. The durable resume cursor remains independently monotonic.
  useEffect(() => {
    if (!sessionContinueCode || !accessCode || isComplete) return undefined;

    const sendHeartbeat = () => {
      if (document.visibilityState !== 'visible') return;
      api.updateSessionProgress(
        sessionContinueCode,
        accessCode,
        Math.max(0, currentQuestionIndex),
        'heartbeat',
        sessionViewEpochRef.current
      ).catch(error => console.warn('Session heartbeat delayed:', error));
    };

    const intervalId = window.setInterval(sendHeartbeat, 30000);
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') sendHeartbeat();
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [sessionContinueCode, accessCode, currentQuestionIndex, isComplete]);

  const setStudentInfo = (name, grade, type) => {
    setStudentName(name);
    setStudentGrade(grade);
    setAssessmentType(type);
  };

  const resetAssessment = () => {
    try {
      for (let index = sessionStorage.length - 1; index >= 0; index -= 1) {
        const key = sessionStorage.key(index);
        if (key?.startsWith('assessnow-writing-draft:')) sessionStorage.removeItem(key);
      }
    } catch {
      // Session storage is optional; encrypted server drafts remain recoverable.
    }
    setAssessmentData(null);
    setCurrentGradeIndex(1);
    setCurrentQuestionIndex(1);
    setStudentResponses({});
    setCorrectAnswers({});
    setConsecutiveFailures(0);
    setMaxCorrectCount(0);
    setPerformanceGradeLevel('');
    setIsComplete(false);
    setTotalGrades(0);
    setTotalQuestions(0);
    setStudentName('');
    setStudentGrade('');
    setAssessmentType('');
    setCurrentPerformanceId(null);
    setAssessmentKind('gradeLevel');
    setGradeScores({});
    setHighestPassedGrade('');
    setSelectedAssessment(null);
    setSessionContinueCode('');
    sessionViewEpochRef.current = '';
    setInputStates({});
    setIsAccessVerified(false);
    // ✅ NEW: Reset timer settings
    setTimerSeconds(null);
    setTimerMode('off');
    setTimingPolicy(null);
    setOverallTiming(normalizeOverallTimeStatus());
    setTimerSettingsLoaded(false);
    setPendingPooledQuestions(null);
    // ✅ NEW: Reset writing results
    setWritingResults({});
    setItemTiming({});
    setWritingDrafts({});
    setNextAssessmentIntent(null);
    persistNextAssessmentIntent(null);
  };

  const prepareAnotherAssessment = () => {
    const learnerAlias = formatStudentNameForDatabase(studentName, studentGrade, accessCode);
    if (!isLearnerAlias(learnerAlias) || !String(studentGrade || '').trim()) {
      throw new Error('The private Learner ID or verified grade is unavailable. Return to the student login and sign in again.');
    }

    const completedAssessmentId = assessmentIdOf(selectedAssessment)
      || assessmentIdOf(assessmentType)
      || assessmentIdOf(assessmentData);

    try {
      for (let index = sessionStorage.length - 1; index >= 0; index -= 1) {
        const key = sessionStorage.key(index);
        if (key?.startsWith('assessnow-writing-draft:')) sessionStorage.removeItem(key);
      }
    } catch {
      // Browser storage is optional. Server-owned attempt state is already final.
    }
    if (sessionContinueCode) securePooledQuestionManager.clearQuestionSet(sessionContinueCode);

    const intent = {
      learnerAlias: String(learnerAlias).trim(),
      gradeName: String(studentGrade).trim(),
      completedAssessmentId: String(completedAssessmentId || '').trim(),
      completedAssessmentName: String(selectedAssessment?.name || assessmentType || '').trim(),
      createdAt: Date.now(),
    };
    setNextAssessmentIntent(intent);
    persistNextAssessmentIntent(intent);
    setAssessmentData(null);
    setCurrentGradeIndex(1);
    setCurrentQuestionIndex(1);
    setStudentResponses({});
    setCorrectAnswers({});
    setConsecutiveFailures(0);
    setMaxCorrectCount(0);
    setPerformanceGradeLevel('');
    setIsComplete(false);
    setTotalGrades(0);
    setTotalQuestions(0);
    setStudentName(String(learnerAlias).trim());
    setStudentGrade(String(studentGrade).trim());
    setAssessmentType('');
    setCurrentPerformanceId(null);
    setAssessmentKind('gradeLevel');
    setGradeScores({});
    setHighestPassedGrade('');
    setSelectedAssessment(null);
    setSessionContinueCode('');
    sessionViewEpochRef.current = '';
    setInputStates({});
    setEvaluatedBlocks([]);
    setTimerSeconds(null);
    setTimerMode('off');
    setTimingPolicy(null);
    setOverallTiming(normalizeOverallTimeStatus());
    setTimerSettingsLoaded(false);
    setPendingPooledQuestions(null);
    setWritingResults({});
    setItemTiming({});
    setWritingDrafts({});
    finalizationInFlightRef.current = null;
  };

  const clearNextAssessmentIntent = () => {
    setNextAssessmentIntent(null);
    persistNextAssessmentIntent(null);
  };

  const handleResumeAssessment = async (continueCode) => {
    try {
      // Do not let settings from a previous login flash onto the resumed test.
      setTimerSeconds(null);
      setTimerMode('off');
      setTimingPolicy(null);
      setOverallTiming(normalizeOverallTimeStatus());
      setTimerSettingsLoaded(false);

      if (!continueCode || !accessCode) {
        throw new Error('Continue code and access code are required');
      }

      if (!/^\d{5}$/.test(continueCode.trim())) {
        throw new Error('Continue code must be exactly 5 digits');
      }

      // ✅ FIX: Don't pass lastQuestionIndex - let server use stored value
      const resumeResponse = await api.resumeSession({
        continueCode: continueCode.trim(),
        accessCode: accessCode.trim(),
        // lastQuestionIndex removed - server will use the stored value from database
      });

      if (!resumeResponse.success || !resumeResponse.session) {
        throw new Error('Server returned unsuccessful resume response');
      }

      const session = resumeResponse.session;
      const assessment = resumeResponse.assessment;
      sessionViewEpochRef.current = String(resumeResponse.viewEpoch || '').trim();
      
      if (!assessment) {
        throw new Error('Assessment data missing from resume response');
      }

      const restoredTimerSeconds = Math.max(
        0,
        Math.min(
          300,
          Number.parseInt(
            resumeResponse.timer_seconds ?? assessment.min_question_time,
            10
          ) || 0
        )
      );
      const restoredTimerMode = normalizeTimerMode(
        resumeResponse.timer_mode,
        restoredTimerSeconds
      );
      const restoredTimingPolicy = normalizeTimingPolicy(
        resumeResponse.timing_policy,
        { mode: restoredTimerMode, defaultSeconds: restoredTimerSeconds }
      );
      setTimerSeconds(restoredTimerSeconds);
      setTimerMode(restoredTimerMode);
      setTimingPolicy(restoredTimingPolicy);
      const restoredOverallTiming = normalizeOverallTimeStatus(resumeResponse);
      setOverallTiming(restoredOverallTiming);
      setItemTiming(resumeResponse.item_timing || {});
      setWritingDrafts(resumeResponse.writing_drafts || {});
      setTimerSettingsLoaded(true);

      setStudentInfo(
        session.student_name,
        session.actual_grade_level,
        session.assessment_id
      );

      setSessionContinueCode(continueCode.trim());
      setCurrentPerformanceId(resumeResponse.performanceId || null);

            const resumeContext = {
        studentName: session.student_name,
        studentGrade: session.actual_grade_level,
        resume: true,
        // ✅ FIX: Use the actual stored value from database, not hardcoded 1
        lastQuestionIndex: session.last_question_index || 1,
        continueCode: continueCode.trim(),
        existingPerformanceId: resumeResponse.performanceId,
        viewEpoch: resumeResponse.viewEpoch || '',
        assessmentType: session.assessment_id,
        accessCode: accessCode.trim(),
        overallTiming: restoredOverallTiming,
        // ✅ NEW: Pass the full session object which includes pooled_question_ids
        session: session,
      };

      const loadedAssessmentData = await loadAssessmentData(
        assessment.fileName,
        assessment,
        resumeContext
      );

      const currentQuestions = loadedAssessmentData.Grades?.reduce(
        (questions, grade) => questions.concat(grade.Questions || []),
        []
      ) || [];
      const initialCorrectAnswers = currentQuestions.reduce((acc, q) => {
        const qId = String(q.ItemNumber);
        acc[qId] = false;
        return acc;
      }, {});

      let storedResponses = {};
      let storedCorrectAnswers = { ...initialCorrectAnswers };

      if (resumeResponse.performanceId) {
        const responseData = await api.getResponses(resumeResponse.performanceId);
        if (!responseData?.responses || typeof responseData.responses !== 'object'
            || Array.isArray(responseData.responses)) {
          throw new Error('Saved responses are unavailable for this attempt');
        }
        const currentQuestionIds = new Set(
          currentQuestions.map(question => String(question.ItemNumber))
        );
        for (const [questionId, responseDataForItem] of Object.entries(responseData.responses)) {
          const qId = String(questionId);
          if (!currentQuestionIds.has(qId)) {
            throw new Error('Saved responses do not match the immutable assessment definition');
          }
          storedResponses[qId] = responseDataForItem.answer;
          storedCorrectAnswers[qId] = responseDataForItem.isCorrect === true;
        }
        setStudentResponses(storedResponses);
        setCorrectAnswers(storedCorrectAnswers);
        if (loadedAssessmentData.assessmentKind === 'regular') {
          handleRegularAssessmentScoring(storedCorrectAnswers, loadedAssessmentData);
        }
      } else {
        setCorrectAnswers(initialCorrectAnswers);
      }

      const totalResumeQuestions = currentQuestions.length;
      const resumeIndex = Math.min(
        Math.max(1, totalResumeQuestions),
        Math.max(1, parseInt(session.last_question_index, 10) || 1)
      );
      setCurrentGradeIndex(loadedAssessmentData.assessmentKind === 'gradeLevel'
        ? Math.floor((resumeIndex - 1) / 5) + 1
        : 1);
      setCurrentQuestionIndex(resumeIndex);

      const result = {
        success: true,
        session: session,
        assessment: assessment,
        student_name: session.student_name,
        actual_grade_level: session.actual_grade_level,
        assessment_id: session.assessment_id,
        fileName: assessment.fileName,
        name: assessment.name,
        last_question_index: resumeIndex,
        resuming: true,
        continueCode: continueCode.trim(),
        performanceId: resumeResponse.performanceId,
        overall_time_limit_seconds: restoredOverallTiming.limitSeconds,
        overall_deadline_at: restoredOverallTiming.deadlineAt,
        overall_remaining_seconds: restoredOverallTiming.remainingSeconds,
        overall_time_expired: restoredOverallTiming.expired,
        resumeTimestamp: new Date().toISOString(),
        totalPreviousResponses: Object.keys(storedResponses).length,
      };

      return result;
    } catch (err) {
      setTimerSeconds(null);
      setTimerMode('off');
      setTimingPolicy(null);
      setItemTiming({});
      setWritingDrafts({});
      setTimerSettingsLoaded(false);
      console.error('🚨 Detailed resume error:', err);
      console.error('🔍 Error Details:', {
        message: err.message,
        stack: err.stack,
        continueCode: continueCode,
        accessCode: accessCode ? 'PRESENT' : 'MISSING',
        timestamp: new Date().toISOString()
      });

      let userFriendlyMessage = 'Failed to resume assessment. ';

      if (err.message.includes('No session found')) {
        userFriendlyMessage += 'Please check your continue code and try again.';
      } else if (err.message.includes('Continue code and access code are required')) {
        userFriendlyMessage += 'Please ensure you have entered both a continue code and are logged in properly.';
      } else if (err.message.includes('Continue code must be exactly 5 digits')) {
        userFriendlyMessage += 'Continue code must be exactly 5 digits.';
      } else if (err.message.includes('Assessment not found')) {
        userFriendlyMessage += 'The assessment for this session is no longer available.';
      } else if (/not unique|ambiguous|more than one performance/i.test(err.message)) {
        userFriendlyMessage += 'The saved assessment has conflicting historical links. An administrator must repair this session before it can be resumed.';
      } else if (/already closed|already finalized|cannot be resumed/i.test(err.message)) {
        userFriendlyMessage += 'This session is already complete or closed. Choose another saved assessment or start a new one.';
      } else if (/no questions available/i.test(err.message)) {
        userFriendlyMessage += 'This saved attempt has no valid question snapshot and cannot be resumed.';
      } else if (/no longer assigned|not assigned and visible/i.test(err.message)) {
        userFriendlyMessage += 'This assessment is no longer assigned and visible for this account.';
      } else {
        userFriendlyMessage += 'Please try again or contact support if the problem persists.';
      }

      const userError = new Error(userFriendlyMessage);
      userError.originalError = err;
      
      throw userError;
    }
  };

  // ✅ SECURITY: Updated loadExistingResponses with server re-validation
  // ✅ FIX: Accept assessmentFileName as parameter to avoid React state timing issues
  const loadExistingResponses = async (assessmentData, studentInfo, assessmentFileName = null) => {
    try {
      const performanceId = studentInfo.existingPerformanceId;
      if (!performanceId) return;
      setCurrentPerformanceId(performanceId);

      const responseData = await api.getResponses(performanceId);
      if (!responseData?.responses || typeof responseData.responses !== 'object'
          || Array.isArray(responseData.responses)) {
        throw new Error('Saved responses are unavailable for this attempt');
      }
      const currentQuestions = assessmentData.Grades?.reduce(
        (questions, grade) => questions.concat(grade.Questions || []),
        []
      ) || [];
      const currentQuestionIds = new Set(
        currentQuestions.map(question => String(question.ItemNumber))
      );
      const storedResponses = {};
      const answersToValidate = [];
      for (const [questionId, responseDataForItem] of Object.entries(responseData.responses)) {
        const qId = String(questionId);
        if (!currentQuestionIds.has(qId)) {
          throw new Error('Saved responses do not match the immutable assessment definition');
        }
        storedResponses[qId] = responseDataForItem.answer;
        answersToValidate.push({
          questionId: qId,
          studentAnswer: responseDataForItem.answer
        });
      }
      if (!answersToValidate.length) return;

      const fileNameToUse = assessmentFileName || selectedAssessment?.fileName;
      if (!fileNameToUse) {
        throw new Error('The immutable assessment filename is unavailable for response validation');
      }
      const validationResults = await validateAnswersBatch(
        answersToValidate,
        fileNameToUse,
        accessCode,
        performanceId
      );
      if (Object.keys(validationResults).length !== answersToValidate.length) {
        throw new Error('Saved responses could not be revalidated completely');
      }
      setStudentResponses(storedResponses);
      setCorrectAnswers(previous => ({ ...previous, ...validationResults }));
      if (assessmentData.assessmentKind === 'regular') {
        handleRegularAssessmentScoring(validationResults, assessmentData);
      }
    } catch (error) {
      console.error('Error loading existing responses:', error);
      throw error;
    }
  };

  // ✅ TIMER FIX: Updated createNewSession function  
  // ✅ POOLED FIX: Save pooled questions to server after session is created
  const createNewSession = async (assessmentData, dbAssessment, studentInfo, pooledQuestions = null) => {
    try {
      const pooledQuestionIds = Array.isArray(pooledQuestions)
        ? pooledQuestions.map(question => question?.ItemNumber ?? question)
        : [];
      // AssessmentLogin may have already created the durable session. Reuse
      // that code so one student start can never create two session rows.
      let newContinueCode = String(studentInfo.continueCode || '').trim();
      let viewEpoch = String(studentInfo.viewEpoch || '').trim();
      if (!newContinueCode) {
        const sessionResponse = await api.post('/sessions', {
          accessCode,
          learnerAlias: studentInfo.studentName,
          actualGradeLevel: studentInfo.studentGrade,
          assessmentId: dbAssessment.id,
          lastQuestionIndex: 0,
          ...(pooledQuestionIds.length ? { pooledQuestionIds } : {})
        });
        newContinueCode = sessionResponse.continueCode;
        viewEpoch = String(sessionResponse.viewEpoch || '').trim();
      }

      setSessionContinueCode(newContinueCode);
      sessionViewEpochRef.current = viewEpoch;

      // ✅ NEW: Save pooled questions to server immediately after session is created
      if (pooledQuestions && pooledQuestions.length > 0) {
        securePooledQuestionManager.saveQuestionSet(newContinueCode, pooledQuestions);
        const pooledSnapshotSaved = await securePooledQuestionManager.saveToServer(
          newContinueCode,
          pooledQuestions,
          accessCode
        );
        if (!pooledSnapshotSaved) {
          throw new Error('The selected pooled questions could not be saved. No attempt was opened.');
        }
        await questionHistoryAPI.saveHistory(
          accessCode,
          dbAssessment.id,
          pooledQuestionIds,
          `session:${newContinueCode}:${dbAssessment.id}`,
          studentInfo.studentName,
          rotationDefinitionRef.current?.assessmentId === dbAssessment.id ? rotationDefinitionRef.current.checksum : null
        );
      }

      const formattedStudentName = formatStudentNameForDatabase(
        studentInfo.studentName,
        studentInfo.studentGrade,
        accessCode.trim()
      );

      const assessmentPool = determineAssessmentPool(assessmentData, dbAssessment);

      // The selected catalog row already contains the exact user's settings,
      // supplied by the server's assessment-ID join. The performance endpoint
      // validates them again, so this requires no extra settings request.
      const ttsEnabled = isEnabledFlag(dbAssessment.tts_enabled) ? 1 : 0;
      const timerSecondsValue = Math.max(
        0,
        Math.min(300, Number.parseInt(dbAssessment.min_question_time, 10) || 0)
      );
      const requestedTimerMode = normalizeTimerMode(
        dbAssessment.timer_mode,
        timerSecondsValue
      );
      const overallTimeLimitSeconds = boundedOverallTimeSeconds(
        dbAssessment.overall_time_limit_seconds
      );

      const performanceData = {
        studentName: formattedStudentName,
        assessmentId: dbAssessment.id,
        assessmentType: dbAssessment.id,
        gradeLevel: studentInfo.studentGrade,
        actualGradeLevel: studentInfo.studentGrade,
        gradePerformance: '',
        code_used: accessCode.trim(),
        assessmentKind: assessmentData.assessmentKind || 'gradeLevel',
        assessmentPool: assessmentPool,
        continueCode: newContinueCode,
        tts_enabled: ttsEnabled,
        ttsEnabled: ttsEnabled,
        timer_seconds: timerSecondsValue,
        timer_mode: requestedTimerMode,
        overall_time_limit_seconds: overallTimeLimitSeconds
      };


      const result = await api.createPerformance(performanceData);
      setCurrentPerformanceId(result.id);

      // The server revalidates the setting at the instant the performance is
      // created. Use the persisted snapshot, not a potentially stale catalog
      // value that was loaded before the teacher's most recent change.
      const savedTimerSeconds = Math.max(
        0,
        Math.min(
          300,
          Number.parseInt(result.timer_seconds ?? timerSecondsValue, 10) || 0
        )
      );
      const savedTtsEnabled = isEnabledFlag(result.tts_enabled) ? 1 : 0;
      const savedTimerMode = normalizeTimerMode(result.timer_mode, savedTimerSeconds);
      const savedTimingPolicy = normalizeTimingPolicy(result.timing_policy, {
        mode: savedTimerMode,
        defaultSeconds: savedTimerSeconds
      });
      const savedOverallTiming = normalizeOverallTimeStatus(
        result,
        overallTimeLimitSeconds
      );
      setTimerSeconds(savedTimerSeconds);
      setTimerMode(savedTimerMode);
      setTimingPolicy(savedTimingPolicy);
      setOverallTiming(savedOverallTiming);
      setItemTiming({});
      setWritingDrafts({});
      setTimerSettingsLoaded(true);
      setSelectedAssessment(current => ({
        ...(current || dbAssessment),
        min_question_time: savedTimerSeconds,
        timer_mode: savedTimerMode,
        overall_time_limit_seconds: savedOverallTiming.limitSeconds,
        tts_enabled: savedTtsEnabled,
      }));

      if (savedTimerSeconds !== timerSecondsValue) {
        console.warn(`⚠️ [NEW SESSION] Timer mismatch: sent ${timerSecondsValue}, got ${result.timer_seconds}`);
      }
          
    } catch (sessionError) {
      console.error('❌ [NEW SESSION] Session Creation Error:', sessionError);
      throw sessionError;
    }
  };

  // ✅ FIXED: Only the loadAssessmentData function with proper syntax
  const loadAssessmentData = async (fileName, dbAssessment, studentInfo) => {
    try {
      setSelectedAssessment(dbAssessment);

      if (studentInfo?.resume) {
        const restoredTimerSeconds = Math.max(0, Math.min(
          300,
          Number.parseInt(
            studentInfo.timerSeconds ?? dbAssessment?.min_question_time,
            10
          ) || 0
        ));
        const restoredTimerMode = normalizeTimerMode(
          studentInfo.timerMode ?? dbAssessment?.timer_mode,
          restoredTimerSeconds
        );
        setTimerSeconds(restoredTimerSeconds);
        setTimerMode(restoredTimerMode);
        setTimingPolicy(normalizeTimingPolicy(
          studentInfo.timingPolicy ?? dbAssessment?.timing_policy,
          { mode: restoredTimerMode, defaultSeconds: restoredTimerSeconds }
        ));
        setOverallTiming(normalizeOverallTimeStatus(
          studentInfo.overallTiming ?? dbAssessment,
          dbAssessment?.overall_time_limit_seconds
        ));
        setItemTiming(studentInfo.itemTiming ?? dbAssessment?.item_timing ?? {});
        setWritingDrafts(studentInfo.writingDrafts ?? dbAssessment?.writing_drafts ?? {});
        setCurrentPerformanceId(studentInfo.existingPerformanceId || null);
        setTimerSettingsLoaded(true);
      }

      // The shared client sends the secure cookie/access context and rejects
      // any accidental answer-bearing payload in the student flow.
      const data = await api.getAssessmentFile(
        fileName,
        studentInfo?.existingPerformanceId || null
      );

      const isPooledFromDB = dbAssessment.assessmentPool === 'Pooled';
      const isPooledFromFile = data.assessmentPool === 'Pooled';
      const isPooled = isPooledFromDB || isPooledFromFile;

      // ✅ Track selected questions for saving after session creation
      let selectedPooledQuestions = null;

      // ✅ FIXED: Enhanced pooled assessment section with comprehensive safety checks
      if (data.assessmentKind === 'regular' && isPooled) {
        if (data.Grades && data.Grades.length > 0 && Array.isArray(data.Grades[0].Questions)) {
          const fullQuestions = [...data.Grades[0].Questions];
          const originalQuestionCount = fullQuestions.length;

          let selectedQuestions;
          let performanceId = null;
          
          // Target number of questions to select
          const targetQuestionCount = Math.min(10, originalQuestionCount);

          if (studentInfo.resume && studentInfo.continueCode && studentInfo.existingPerformanceId) {
            // A performance makes the server snapshot immutable. Resume must
            // recover that exact ordered set and must never manufacture a
            // replacement from browser storage, history, or a random seed.
            selectedQuestions = await securePooledQuestionManager.recoverFromServer(
              studentInfo.continueCode,
              accessCode,
              fullQuestions
            );
            const recoveredIds = selectedQuestions?.map(question => String(question.ItemNumber)) || [];
            if (selectedQuestions?.length !== targetQuestionCount
                || new Set(recoveredIds).size !== recoveredIds.length) {
              throw new Error('The saved pooled question set could not be restored exactly. This attempt was not changed.');
            }
          } else {
            
            const numToSelect = targetQuestionCount;
            const seed = studentInfo.continueCode ? parseInt(studentInfo.continueCode) : null;
            const history = await questionHistoryAPI.getHistory(accessCode, dbAssessment.id);
            rotationDefinitionRef.current = { assessmentId: dbAssessment.id, checksum: history.definitionChecksum };
            selectedQuestions = selectRotationQuestions(fullQuestions, history, numToSelect, seed);

            // ✅ CRITICAL VERIFICATION: Check question count BEFORE saving          
            if (!selectedQuestions || selectedQuestions.length !== numToSelect) {
              console.error(`❌ CRITICAL ERROR: Expected ${numToSelect} questions but got ${selectedQuestions?.length || 0}`);
              console.error(`❌ This indicates a bug in selectQuestionsWithTopicDistribution function`);
              
              // Emergency fallback - direct random selection without topic distribution
              const usedIds = new Set(selectedQuestions?.map(q => q.ItemNumber) || []);
              const availableQuestions = fullQuestions.filter(q => !usedIds.has(q.ItemNumber));
              
              if (availableQuestions.length >= numToSelect) {
                selectedQuestions = seed 
                  ? getSeededRandomQuestions(availableQuestions, numToSelect, seed)
                  : deepCopyAndShuffle(availableQuestions, numToSelect);
              } else {
                // Last resort - just take the first N questions
                selectedQuestions = fullQuestions.slice(0, numToSelect);
              }
            }
            
            // ✅ FIXED: Store pooled questions to be saved after session is created
            // For new assessments, we don't have continueCode yet - it's created in createNewSession
            selectedPooledQuestions = selectedQuestions;
          }

          // ✅ FINAL SAFETY CHECKS AND VERIFICATION
          
          // Check for null/undefined
          if (!selectedQuestions) {
            console.error(`❌ FATAL: selectedQuestions is null/undefined`);
            selectedQuestions = fullQuestions.slice(0, targetQuestionCount);
          }
          
          // Check for wrong count
          if (selectedQuestions.length !== targetQuestionCount) {
            console.error(`❌ FINAL COUNT MISMATCH: Expected ${targetQuestionCount}, got ${selectedQuestions.length}`);
            
            if (selectedQuestions.length < targetQuestionCount) {
              // Need more questions
              const usedIds = new Set(selectedQuestions.map(q => q.ItemNumber));
              const unusedQuestions = fullQuestions.filter(q => !usedIds.has(q.ItemNumber));
              const needed = targetQuestionCount - selectedQuestions.length;
              const additional = unusedQuestions.slice(0, needed);
              
              selectedQuestions = [...selectedQuestions, ...additional];
            } else if (selectedQuestions.length > targetQuestionCount) {
              // Too many questions
              selectedQuestions = selectedQuestions.slice(0, targetQuestionCount);
            }
          }
          
          // Check for duplicates
          const questionIds = selectedQuestions.map(q => q.ItemNumber);
          const uniqueIds = new Set(questionIds);
          
          if (questionIds.length !== uniqueIds.size) {
            console.error(`❌ DUPLICATE QUESTIONS: ${questionIds.length} total, ${uniqueIds.size} unique`);
            console.error(`❌ Duplicate IDs:`, questionIds.filter((id, index, arr) => arr.indexOf(id) !== index));
            
            // Remove duplicates
            const seen = new Set();
            selectedQuestions = selectedQuestions.filter(q => {
              if (seen.has(q.ItemNumber)) {
                return false;
              }
              seen.add(q.ItemNumber);
              return true;
            });
            
            // Fill missing slots
            if (selectedQuestions.length < targetQuestionCount) {
              const usedIds = new Set(selectedQuestions.map(q => q.ItemNumber));
              const unusedQuestions = fullQuestions.filter(q => !usedIds.has(q.ItemNumber));
              const needed = targetQuestionCount - selectedQuestions.length;
              const additional = unusedQuestions.slice(0, needed);
              selectedQuestions = [...selectedQuestions, ...additional];
            }
          }
          
          // Final validation
          if (selectedQuestions.length !== targetQuestionCount) {
            console.error(`❌ FINAL VALIDATION FAILED: Still have ${selectedQuestions.length} instead of ${targetQuestionCount}`);
            // Last resort - force correct count
            if (selectedQuestions.length > targetQuestionCount) {
              selectedQuestions = selectedQuestions.slice(0, targetQuestionCount);
            } else {
              // Pad with random questions
              while (selectedQuestions.length < targetQuestionCount && selectedQuestions.length < fullQuestions.length) {
                const usedIds = new Set(selectedQuestions.map(q => q.ItemNumber));
                const unused = fullQuestions.find(q => !usedIds.has(q.ItemNumber));
                if (unused) {
                  selectedQuestions.push(unused);
                } else {
                  break;
                }
              }
            }
          } else {
          }
          data.Grades[0].Questions = selectedQuestions;
          
          // Update the tracker
          selectedPooledQuestions = selectedQuestions;
        }
      }

      const totalQs = data.Grades.reduce(
        (sum, grade) => sum + (grade.Questions ? grade.Questions.length : 0),
        0
      );

      setTotalQuestions(totalQs);
      setTotalGrades(data.Grades.length);

      // ✅ SECURITY: Initialize correctAnswers with false (server will validate)
      const allQuestions = data.Grades.reduce(
        (acc, grade) => acc.concat(grade.Questions || []),
        []
      );
      const initialCorrectAnswers = {};
      allQuestions.forEach(q => {
        initialCorrectAnswers[String(q.ItemNumber)] = false;
      });
      setCorrectAnswers(initialCorrectAnswers);

      if (studentInfo.resume && studentInfo.continueCode) {
        setSessionContinueCode(studentInfo.continueCode);
        setIsResumedSession(true);

        if (studentInfo.lastQuestionIndex) {
          const resumeIndex = Math.min(
            Math.max(1, totalQs),
            Math.max(1, parseInt(studentInfo.lastQuestionIndex, 10) || 1)
          );
          setCurrentGradeIndex(data.assessmentKind === 'gradeLevel'
            ? Math.floor((resumeIndex - 1) / 5) + 1
            : 1);
          setCurrentQuestionIndex(resumeIndex);
        }

        if (studentInfo.existingPerformanceId) {
          // Restore only the exact performance joined to the continue code.
          await loadExistingResponses(data, studentInfo, fileName);
        } else {
          // A crash can leave the durable session created but no performance.
          // Reuse that same continue code and create the missing attempt once.
          await createNewSession(data, dbAssessment, studentInfo, selectedPooledQuestions);
        }
      } else {
        // ✅ FIXED: Pass pooled questions to createNewSession so they can be saved after session is created
        await createNewSession(data, dbAssessment, studentInfo, selectedPooledQuestions);
      }

      setAssessmentKind(data.assessmentKind || 'gradeLevel');
      setAssessmentData(data);

      return data;
    } catch (error) {
      // Never render or accept answers for an assessment that failed to obtain
      // its immutable, server-backed performance record.
      setAssessmentData(null);
      setCurrentPerformanceId(null);
      console.error('Assessment loading error:', error);
      throw error;
    }
  };

  const handleRegularAssessmentScoring = (preloadedCorrectAnswers = null, scoringAssessmentData = assessmentData) => {
    if (!scoringAssessmentData || !scoringAssessmentData.Grades || !scoringAssessmentData.Grades[0]) {
      console.warn('Cannot perform scoring - no assessment data');
      return;
    }
    
    const questions = scoringAssessmentData.Grades[0].Questions || [];
    const answersToUse = preloadedCorrectAnswers || correctAnswers;
    
    let correctCount = 0;
    const scoringDetails = [];

    questions.forEach(q => {
      const qId = String(q.ItemNumber);
      const isCorrect = answersToUse[qId] === true;
      
      if (isCorrect) {
        correctCount++;
      }
      
      scoringDetails.push({
        questionId: qId,
        isCorrect,
        answerValue: answersToUse[qId]
      });
    });

    if (questions.length > 0) {
      const pct = Math.round((correctCount / questions.length) * 100);
      setPerformanceGradeLevel(`${pct}%`);
    }
  };

  // A response becomes locally committed only after the authoritative server
  // accepts it. Navigation therefore cannot advance past an unsaved item.
  const recordAnswer = async (questionId, answer, questionType, unusedCorrectAnswer) => {
    const qKey = String(questionId);
    if (!currentPerformanceId) {
      throw new Error('No performance ID is available; the answer was not submitted');
    }
    try {
      // /api/responses is the one authoritative scoring boundary. It validates
      // delivery membership, derives the question type from the immutable
      // attempt snapshot, scores ordinary/writing answers, and safely records
      // oral reading as awaiting teacher score in one database transaction.
      const saved = await ResponseModel.createMany(currentPerformanceId, {
        [qKey]: { answer, isCorrect: false }
      });
      const accepted = saved?.responses?.[qKey];
      if (typeof accepted?.isCorrect !== 'boolean') {
        throw new Error('The server did not confirm the submitted response');
      }
      const authoritativeCorrect = accepted.isCorrect;
      setStudentResponses(previous => ({ ...previous, [qKey]: answer }));
      setCorrectAnswers(previous => ({ ...previous, [qKey]: authoritativeCorrect }));
      if (assessmentKind === 'regular') {
        handleRegularAssessmentScoring({
          ...correctAnswers,
          [qKey]: authoritativeCorrect
        });
      }
      return authoritativeCorrect;
    } catch (error) {
      console.error('Answer submission failed; remaining on the current item:', error);
      throw error;
    }
  };

  // ✅ NEW: Writing composition answer handler
  // Called by WritingComposition component after 360-IF scoring
  const handleWritingAnswer = async (questionId, text, questionType, rubricResult) => {
    const qKey = String(questionId);
    console.log('[handleWritingAnswer] Storing response:', { questionId, qKey, textLength: text?.length });

    // For writing, "correct" is defined by percentage threshold
    const allQuestions = assessmentData?.Grades?.reduce(
      (acc, grade) => acc.concat(grade.Questions || []),
      []
    ) || [];
    const currentQ = allQuestions.find(q => String(q.ItemNumber) === qKey);
    const threshold = currentQ?.PassingThreshold || 60;
    const isCorrect = rubricResult.percentage >= threshold;

    // Persist before marking the item as scored locally. This prevents Next
    // from enabling when the encrypted server has not accepted the response.
    let authoritativeRubric = rubricResult;
    let authoritativeCorrect = isCorrect;
    if (currentPerformanceId) {
      const saved = await api.saveResponses(currentPerformanceId, {
        [qKey]: {
          answer: text,
          isCorrect,
          questionType: 'writingComposition',
          rubricScores: JSON.stringify(rubricResult)
        }
      });
      const accepted = saved?.responses?.[qKey];
      if (accepted?.rubricScores) authoritativeRubric = accepted.rubricScores;
      if (typeof accepted?.isCorrect === 'boolean') authoritativeCorrect = accepted.isCorrect;
    }

    setStudentResponses(prev => ({ ...prev, [qKey]: text }));
    setWritingResults(prev => ({ ...prev, [qKey]: authoritativeRubric }));
    setCorrectAnswers(prev => ({ ...prev, [qKey]: authoritativeCorrect }));
    setWritingDrafts(previous => {
      const updated = { ...previous };
      delete updated[qKey];
      return updated;
    });

    if (assessmentKind === 'regular') {
      handleRegularAssessmentScoring();
    }
    return { isCorrect: authoritativeCorrect, rubricResult: authoritativeRubric };
  };

  const submitResponses = async (performanceId) => {
    try {
      const formatted = {};
      Object.entries(studentResponses).forEach(([qId, ans]) => {
        formatted[qId] = {
          answer: ans,
          isCorrect: !!correctAnswers[qId]
        };
      });

      const response = await fetch(`${API_BASE_URL}/responses`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ performanceId, responses: formatted })
      });

      if (!response.ok) {
        throw new Error('Failed to save responses');
      }
      return await response.json();
    } catch (err) {
      console.error('Error submitting responses:', err);
      throw err;
    }
  };

  const findLowestGradeIndex = (data) => {
    if (!data || !data.Grades || !data.Grades.length) return 0;
    const gradeOrder = [
      'pre-k', 'prek', 'pre k', 'kindergarten', 'k',
      'first', '1st', 'grade 1',
      'second', 'third', 'fourth',
      'fifth', 'sixth', 'seventh',
      'eight', 'ninth'
    ];
    let lowestIndex = 0;
    let lowestGradeName = data.Grades[0].Grade.toLowerCase();
    for (let i = 0; i < data.Grades.length; i++) {
      const name = data.Grades[i].Grade.toLowerCase();
      const curIdx = gradeOrder.findIndex((g) => name.includes(g));
      const lowIdx = gradeOrder.findIndex((g) => lowestGradeName.includes(g));
      if (curIdx !== -1 && (lowIdx === -1 || curIdx < lowIdx)) {
        lowestGradeName = name;
        lowestIndex = i;
      }
    }
    return lowestIndex;
  };

  const evaluateGradeLevelBlock = async () => {
    if (assessmentKind !== 'gradeLevel') {
      return false;
    }
  
    if (currentQuestionIndex % 5 !== 0) return false;
  
    const blockIndex = Math.floor((currentQuestionIndex - 1) / 5);
  
    if (evaluatedBlocks.includes(blockIndex)) {
      return false;
    }
  
    const allQuestions = assessmentData?.Grades?.reduce(
      (acc, grade) => acc.concat(grade.Questions || []),
      []
    ) || [];
    const currentGradeData = assessmentData.Grades[currentGradeIndex - 1];
  
    const gradeEnd = currentQuestionIndex;
    const gradeStart = gradeEnd - 4;
    let correctCount = 0;
  
    for (let i = gradeStart; i <= gradeEnd; i++) {
      const itemNumber = allQuestions[i - 1]?.ItemNumber;
      if (itemNumber && correctAnswers[itemNumber]) {
        correctCount++;
      }
    }
  
    setEvaluatedBlocks(prev => [...prev, blockIndex]);
  
    const isPassed = correctCount >= 3;
    if (isPassed) {
      setHighestPassedGrade(currentGradeData.Grade);
      setConsecutiveFailures(0);
    } else {
      const newFailCount = consecutiveFailures + 1;
      setConsecutiveFailures(newFailCount);
  
      if (newFailCount >= 3) {
        setIsComplete(true);
        finalizeAssessment();
        return true;
      }
    }
    return false;
  };

  // ✅ SECURITY: Updated handleNextOrComplete to remove correctAnswer parameter
  const handleNextOrComplete = async () => {
    if (isComplete) {
      finalizeAssessment();
      return;
    }

    const allQuestions =
      assessmentData?.Grades?.reduce(
        (acc, grade) => acc.concat(grade.Questions || []),
        []
      ) || [];
    const currentQuestion = allQuestions[currentQuestionIndex - 1];
    if (!currentQuestion) {
      console.error('No valid current question found - cannot proceed');
      return;
    }

    // ✅ NEW: Writing composition questions use their own submit flow
    // WritingComposition component handles scoring + recording via handleWritingAnswer
    // Here we just check if answer exists and navigate
    if (currentQuestion.questionType === 'writingComposition' || currentQuestion.QuestionType === 'writingComposition') {
      const qKey = String(currentQuestion.ItemNumber);
      if (!studentResponses[qKey]) {
        // No answer yet - WritingComposition hasn't submitted
        return;
      }
      // Answer exists (submitted via WritingComposition) — navigate forward
      if (currentQuestionIndex >= totalQuestions) {
        await finalizeAssessment();
      } else {
        const nextQuestionIndex = currentQuestionIndex + 1;
        await navigateQuestion('next', { answeredItemNumber: currentQuestion.ItemNumber });
      }
      return;
    }

    if (
      isResumedSession &&
      (currentQuestion.questionType === 'multipleChoice' ||
        currentQuestion.questionType === 'trueFalse')
    ) {
      const selectedValue = getSelectedRadioValue(currentQuestion.ItemNumber);
      if (selectedValue && !studentResponses[currentQuestion.ItemNumber]) {
        try {
          // ✅ SECURITY: No correctAnswer parameter passed
          await recordAnswer(
            currentQuestion.ItemNumber,
            selectedValue,
            currentQuestion.questionType
          );
        } catch (err) {
          console.error('Error recording selection in resumed session:', err);
          return;
        }
      }
      if (!selectedValue && !studentResponses[currentQuestion.ItemNumber]) {
        return;
      }
    }

    if (
      !studentResponses[currentQuestion.ItemNumber] &&
      inputStates[currentQuestion.ItemNumber]
    ) {
      const value = inputStates[currentQuestion.ItemNumber].trim().toLowerCase() || '';
      if (value) {
        const processedAnswer = handleAnswerSubmission(
          currentQuestion.questionType,
          value,
          null
        );
        // ✅ SECURITY: No correctAnswer parameter passed
        await recordAnswer(
          currentQuestion.ItemNumber,
          processedAnswer,
          currentQuestion.questionType
        );
      }
    }

    const hasAnswer = !!studentResponses[currentQuestion.ItemNumber];
    if (!hasAnswer) {
      return;
    }

    if (assessmentKind === 'gradeLevel') {
      const terminated = await evaluateGradeLevelBlock();
      if (terminated) {
        return;
      }
    }

    if (currentQuestionIndex >= totalQuestions) {
      finalizeAssessment();
      return;
    }

    const nextQuestionIndex = currentQuestionIndex + 1;
    await navigateQuestion('next', { answeredItemNumber: currentQuestion.ItemNumber });
  };

  const saveAssessmentItemState = useCallback(async (questionNumber, state) => {
    if (!currentPerformanceId || !questionNumber) return null;
    const qKey = String(questionNumber);
    const result = await api.saveAssessmentItemState(
      currentPerformanceId,
      { questionNumber, ...state },
      accessCode
    );
    if (result?.timing) {
      setItemTiming(previous => ({
        ...previous,
        [qKey]: result.timing
      }));
    }
    if (Object.hasOwn(state || {}, 'draftText')) {
      setWritingDrafts(previous => {
        const updated = { ...previous };
        if (result?.draftCleared) delete updated[qKey];
        else updated[qKey] = { text: state.draftText, updatedAt: new Date().toISOString() };
        return updated;
      });
    }
    return result;
  }, [currentPerformanceId, accessCode]);

  const navigateQuestion = async (direction, options = {}) => {
    if (isComplete) return;

    const allQuestions = assessmentData?.Grades?.reduce(
      (questions, grade) => questions.concat(grade.Questions || []),
      []
    ) || [];

    if (consecutiveFailures >= 3 && assessmentKind === 'gradeLevel') {
      await finalizeAssessment();
      return;
    }

    if (direction === 'next') {
      const currentQuestion = allQuestions[currentQuestionIndex - 1];
      if (currentQuestion) {
        const itemNumber = currentQuestion.ItemNumber;
        const answerWasCommittedThisTurn = String(options.answeredItemNumber) === String(itemNumber);
        if (!studentResponses[itemNumber] && !answerWasCommittedThisTurn) {
          console.warn('Cannot proceed without answering current question', { itemNumber });
          return;
        }
      }
      if (currentQuestionIndex >= allQuestions.length) {
        await finalizeAssessment();
        return;
      }

      const nextQuestionIndex = currentQuestionIndex + 1;
      if (sessionContinueCode) {
        const progress = await api.updateSessionProgress(
          sessionContinueCode,
          accessCode,
          nextQuestionIndex,
          'navigation',
          sessionViewEpochRef.current
        );
        const acceptedQuestionIndex = Number(progress?.acceptedQuestionIndex);
        if (!progress?.success || !Number.isInteger(acceptedQuestionIndex)
            || acceptedQuestionIndex < nextQuestionIndex) {
          throw new Error('The next-question position was not durably accepted');
        }
      }
      setCurrentQuestionIndex(nextQuestionIndex);
      if (assessmentKind === 'gradeLevel' && currentQuestionIndex % 5 === 0) {
        setCurrentGradeIndex(previous => previous + 1);
      }
      return;
    }

    if (direction === 'back' && currentQuestionIndex > 1) {
      const previousQuestionIndex = currentQuestionIndex - 1;
      setCurrentQuestionIndex(previousQuestionIndex);
      if (assessmentKind === 'gradeLevel' && previousQuestionIndex % 5 === 0) {
        setCurrentGradeIndex(previous => Math.max(previous - 1, 1));
      }
      if (sessionContinueCode) {
        await api.updateSessionProgress(
          sessionContinueCode,
          accessCode,
          previousQuestionIndex,
          'view',
          sessionViewEpochRef.current
        );
      }
    }
  };

  const updateSessionProgress = async (questionIndex, activityType = 'navigation') => {
    if (!sessionContinueCode || !accessCode) {
      return { success: false, message: 'No continue code or access code available' };
    }
    try {
      return await api.updateSessionProgress(
        sessionContinueCode,
        accessCode,
        questionIndex,
        activityType,
        sessionViewEpochRef.current
      );
    } catch (error) {
      console.error('Error updating session progress:', error);
      return { success: false, error: error.message };
    }
  };

  const getSelectedRadioValue = (questionId) => {
    const selectedRadio = document.querySelector(`input[name="question-${questionId}"]:checked`);
    return selectedRadio ? selectedRadio.value : null;
  };

  const getButtonDisabledState = () => {
    if (!assessmentData) return true;
    const allQuestions =
      assessmentData?.Grades?.reduce(
        (acc, grade) => acc.concat(grade.Questions || []),
        []
      ) || [];
    const currentQuestion = allQuestions[currentQuestionIndex - 1];
    if (!currentQuestion) return true;
    const questionId = currentQuestion.ItemNumber;

    // ✅ NEW: Writing questions — check if response exists (submitted via WritingComposition)
    const qType = currentQuestion.questionType || currentQuestion.QuestionType || '';
    if (qType === 'writingComposition') {
      return !studentResponses[String(questionId)];
    }

    if (
      isResumedSession &&
      (currentQuestion.questionType === 'multipleChoice' ||
        currentQuestion.questionType === 'trueFalse')
    ) {
      return !getSelectedRadioValue(questionId);
    }
    return !studentResponses[questionId] && !inputStates[questionId];
  };

  useEffect(() => {
    const allQuestions =
      assessmentData?.Grades?.reduce(
        (acc, grade) => acc.concat(grade.Questions || []),
        []
      ) || [];
    const currentQuestion = allQuestions[currentQuestionIndex - 1];
    if (isResumedSession && currentQuestion) {
      const radios = document.querySelectorAll(
        `input[name="question-${currentQuestion.ItemNumber}"]`
      );
      radios.forEach((radio) => {
        radio.checked = false;
      });
    }
  }, [currentQuestionIndex, isResumedSession, assessmentData]);

  useEffect(() => {
    if (isComplete) {
      // Additional handling if needed when assessment is complete.
    }
  }, [isComplete, assessmentKind]);

  useEffect(() => {
    if (
      isResumedSession &&
      assessmentData &&
      assessmentKind === 'regular' &&
      Object.keys(studentResponses).length > 0
    ) {
      handleRegularAssessmentScoring();
    }
  }, [isResumedSession, assessmentData, assessmentKind, studentResponses]);

  // ✅ SECURITY: Updated finalizeAssessment to clean up secure storage
  const finalizeAssessmentOperation = async (options = {}) => {
    const completionReason = options?.reason === 'time_expired'
      ? 'time_expired'
      : 'student_submit';
    let finalPerformance = '';
    if (assessmentKind === 'regular') {
      finalPerformance = calculateRegularFinalScore();
    } else {
      const gradesArray = assessmentData.Grades || [];

      // Diagnostic contract: each grade-level block contains exactly five
      // items, and 3/5 is the local instructional performance flag.
      const blockScores = gradesArray.map((gradeBlock, index) => {
        const questions = gradeBlock.Questions || [];
        const correctCount = questions.reduce(
          (count, question) => count + (correctAnswers[question.ItemNumber] ? 1 : 0),
          0
        );
        return { index, correctCount };
      });
      const meetingBlocks = blockScores.filter(block => block.correctCount >= 3);
      const selectedIndex = meetingBlocks.length > 0
        ? Math.max(...meetingBlocks.map(block => block.index))
        : -1;

      if (selectedIndex >= 0) {
        finalPerformance = gradesArray[selectedIndex].Grade;
      } else {
        finalPerformance = `Below ${gradesArray[0]?.Grade || 'PreK'}`;
      }
    }

    if (!currentPerformanceId) {
      throw new Error('This assessment has no performance record and cannot be finalized safely');
    }
    if (completionReason === 'time_expired') {
      const expiration = await api.expirePerformance(
        currentPerformanceId,
        options?.finalResponse || null
      );
      finalPerformance = expiration.grade_performance ?? finalPerformance;
      setOverallTiming(previous => ({ ...previous, remainingSeconds: 0, expired: true }));
    } else {
      const completion = await PerformanceModel.update(currentPerformanceId, {
        isComplete: true
      });
      finalPerformance = completion?.grade_performance ?? finalPerformance;
    }
    if (sessionContinueCode) {
      securePooledQuestionManager.clearQuestionSet(sessionContinueCode);
    }
    setPerformanceGradeLevel(finalPerformance);
    setIsComplete(true);
    await api.clearResponseOutbox(currentPerformanceId).catch(error => {
      console.warn('Completed assessment response outbox cleanup was deferred:', error);
    });
    try {
      for (let index = sessionStorage.length - 1; index >= 0; index -= 1) {
        const key = sessionStorage.key(index);
        if (key?.startsWith('assessnow-writing-draft:')) sessionStorage.removeItem(key);
      }
    } catch {
      // No local draft exists when session storage is unavailable.
    }

    // Completion is server-authoritative. The performance lifecycle transition
    // removes it from active session monitoring; a student must never call the
    // manager-only session filtering/deletion endpoints from this screen.
    
    console.log(`[finalizeAssessment] Assessment finalized (${completionReason})`);
    return { completionReason, gradePerformance: finalPerformance };
  };

  // React state updates are not synchronous enough to guard rapid double
  // activation. Every caller shares the same in-flight finalization promise.
  const finalizeAssessment = (options = {}) => {
    if (finalizationInFlightRef.current) return finalizationInFlightRef.current;
    const operation = finalizeAssessmentOperation(options);
    finalizationInFlightRef.current = operation;
    operation.then(
      () => {
        if (finalizationInFlightRef.current === operation) finalizationInFlightRef.current = null;
      },
      () => {
        if (finalizationInFlightRef.current === operation) finalizationInFlightRef.current = null;
      }
    );
    return operation;
  };

  const calculateRegularFinalScore = () => {
    if (!assessmentData || !assessmentData.Grades || !assessmentData.Grades[0]) {
      console.warn('No assessment data available for scoring');
      return '';
    }
    
    const questions = assessmentData.Grades[0].Questions || [];
    
    if (questions.length === 0) {
      console.warn('No questions found in assessment');
      return '0%';
    }
    
    const currentQuestionIds = questions.map(q => String(q.ItemNumber));
    
    let correctCount = 0;
    const detailedResults = [];
    
    questions.forEach(question => {
      const qId = String(question.ItemNumber);
      const isCorrect = correctAnswers[qId] === true;
      
      if (isCorrect) {
        correctCount++;
      }
      
      detailedResults.push({
        questionId: qId,
        isCorrect,
        hasResponse: studentResponses[qId] !== undefined,
        response: studentResponses[qId]
      });
    });
        
    const responsesForCurrentQuestions = Object.keys(studentResponses).filter(qId => 
      currentQuestionIds.includes(qId)
    );
    const correctAnswersForCurrentQuestions = Object.keys(correctAnswers).filter(qId => 
      currentQuestionIds.includes(qId) && correctAnswers[qId] === true
    );
        
    const totalResponses = Object.keys(studentResponses).length;
    const totalCorrectAnswers = Object.keys(correctAnswers).filter(qId => correctAnswers[qId] === true).length;
    
    if (totalResponses > responsesForCurrentQuestions.length) {
      console.warn(`⚠️ POTENTIAL DATA LOSS: Found ${totalResponses} total responses but only ${responsesForCurrentQuestions.length} match current questions`);
      console.warn('This might indicate a pooled question set mismatch');
    }
    
    if (totalCorrectAnswers > correctAnswersForCurrentQuestions.length) {
      console.warn(`⚠️ POTENTIAL SCORE LOSS: Found ${totalCorrectAnswers} total correct answers but only ${correctAnswersForCurrentQuestions.length} match current questions`);
    }
    
    const percentage = Math.round((correctCount / questions.length) * 100);
        
    return `${percentage}%`;
  };

  const validateResumeData = (assessmentData, correctAnswers, studentResponses, continueCode) => {
   
    if (!assessmentData?.Grades?.[0]?.Questions) {
      console.error('❌ Invalid assessment data structure');
      return false;
    }
    
    const currentQuestions = assessmentData.Grades[0].Questions;
    const currentQuestionIds = currentQuestions.map(q => String(q.ItemNumber));    
    const externalResponses = Object.keys(studentResponses).filter(qId => 
      !currentQuestionIds.includes(qId)
    );
    
    if (externalResponses.length > 0) {
      console.warn(`⚠️ Found ${externalResponses.length} responses for questions not in current set:`, externalResponses);
    }
    
    const externalCorrectAnswers = Object.keys(correctAnswers).filter(qId => 
      !currentQuestionIds.includes(qId) && correctAnswers[qId] === true
    );
    
    if (externalCorrectAnswers.length > 0) {
      console.warn(`⚠️ Found ${externalCorrectAnswers.length} correct answers for questions not in current set:`, externalCorrectAnswers);
      console.warn('This indicates potential pooled question set mismatch');
    }
    
    if (continueCode) {
      try {
        const savedQuestions = securePooledQuestionManager.getQuestionSet(continueCode);
        if (savedQuestions) {
          const savedQuestionIds = savedQuestions.map(q => String(q.ItemNumber));
          const idsMatch = JSON.stringify(currentQuestionIds.sort()) === JSON.stringify(savedQuestionIds.sort());
          
          if (!idsMatch) {
            console.error('❌ CRITICAL: Current question IDs do not match saved pooled questions');
            console.error('Current:', currentQuestionIds);
            console.error('Saved:', savedQuestionIds);
            return false;
          } else {
          }
        } else {
          console.warn('⚠️ No saved pooled questions found in sessionStorage');
        }
      } catch (error) {
        console.error('❌ Error validating pooled questions:', error);
      }
    }
    
    return true;
  };

  return {
    assessmentData,
    currentGradeIndex,
    currentQuestionIndex,
    studentResponses,
    correctAnswers,
    isComplete,
    performanceGradeLevel,
    maxCorrectCount,
    totalGrades,
    totalQuestions,
    studentName,
    studentGrade,
    assessmentType,
    accessCode,
    assessmentKind,
    highestPassedGrade,
    gradeScores,
    selectedAssessment,
    currentPerformanceId,
    sessionContinueCode,
    setSessionContinueCode,
    inputStates,
    setInputStates,
    setStudentInfo,
    setAccessCode,
    setAssessmentKind,
    loadAssessmentData,
    recordAnswer,
    handleWritingAnswer,
    writingResults,
    writingDrafts,
    itemTiming,
    saveAssessmentItemState,
    submitResponses,
    navigateQuestion,
    finalizeAssessment,
    resetAssessment,
    prepareAnotherAssessment,
    nextAssessmentIntent,
    clearNextAssessmentIntent,
    handleResumeAssessment,
    updateSessionProgress,
    handleNextOrComplete,
    getButtonDisabledState,
    evaluateGradeLevelBlock,
    getSelectedRadioValue,
    isAccessVerified,
    setIsAccessVerified,
    // ✅ NEW: Expose timer settings for timer component
    timerSettings: {
      timerSeconds: timerSeconds,
      timerMode,
      timingPolicy,
      overallTiming,
      settingsLoaded: timerSettingsLoaded,
      assessmentType: selectedAssessment?.name || assessmentType
    }
  };
};
